import './AlosCalculator.css'
import { Helmet } from 'react-helmet-async';
import ArrowRight from '../../icons/ArrowRight';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';
import ToolFixedMenu from './ToolFixedMenu';


function ALOSCalculator() {
    const langPrefix = useLanguagePrefix();
    const [roomNights, setRoomNights] = useState(null);
    const [numberOfReservation, setNumberOfReservation] = useState(null);
    const [ALOS, setALOS] = useState(null);
    const { t } = useTranslation();


    const handleInputChange = (e, type) => {
        const value = e.target.value;
        if (type === 'nights') {
            setRoomNights(value);
        } else if (type === 'reservation') {
            setNumberOfReservation(value);
        }
    };


    const calculateALOS = () => {
        if (roomNights > 0 && numberOfReservation > 0) {
            const result = roomNights / numberOfReservation;
            const formatted = Number.isInteger(result) ? result : result.toFixed(2);
            setALOS(formatted);
        } else {
            setALOS(null);
        }
    };

    return (
        <div> 

            <title>ALOS Calculator – Average Length of Stay Tool for Hotels - Prostay</title>
            <meta name="description" content="Use Prostay’s free ALOS calculator to measure your hotel’s average guest stay duration. Analyze booking trends and optimize rate strategies based on guest behavior." />
            <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/alos-calculator/"} />

             <link rel="alternate" href="https://www.prostay.com/es/alos-calculator/" />
             <link rel="alternate" href="https://www.prostay.com/en/alos-calculator/" />

            <meta property="og:title" content="ALOS Calculator – Average Length of Stay Tool for Hotels - Prostay" />
            <meta property="og:description" content="Use Prostay’s free ALOS calculator to measure your hotel’s average guest stay duration. Analyze booking trends and optimize rate strategies based on guest behavior." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/alos-calculator/"} />
            <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

            <meta property="og:site_name" content="Prostay" />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="ALOS Calculator – Average Length of Stay Tool for Hotels - Prostay" />
            <meta name="twitter:description" content="Use Prostay’s free ALOS calculator to measure your hotel’s average guest stay duration. Analyze booking trends and optimize rate strategies based on guest behavior." />
            <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
            <meta name="twitter:site" content="@prostaypms" />
            <meta name="twitter:creator" content="@prostaypms" />
            <div className='d-flex alos-calculator flex-column-reverse flex-xl-row'>
                <div className='d-flex flex-column width-left gap-4'>
                    <div>
                        <h1 style={{ margin: '0px' }}>
                            {t('ALOS Calculator for Instant Length‑of‑Stay Calculation')}
                        </h1>
                        <h6 className='mt-4'>
                            {t('Track Guest Stay Trends with ALOS.')} <br />
                            {t('Use this free ALOS calculator to understand how long guests stay on average — and make smarter decisions around pricing, promotions, and operations.')}
                        </h6>
                        <div className='d-flex flex-column-reverse flex-xl-row gap-2 gap-sm-5 mt-5'>
                            <div className='d-flex flex-column gap-4'>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Total Room Nights')}
                                    </label>
                                    <input
                                        type='number'
                                        value={roomNights}
                                        onChange={(e) => handleInputChange(e, 'nights')}
                                        placeholder={t('Enter amount here')} />
                                </div>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Total Number of Reservations')}
                                    </label>
                                    <input
                                        type='number'
                                        value={numberOfReservation}
                                        onChange={(e) => handleInputChange(e, 'reservation')}
                                        placeholder={t('Enter number here')} />
                                </div>
                                <div className='d-flex gap-3 gap-sm-5 flex-column flex-sm-row align-items-center'>
                                    <button className='light-blue-filled-btn-xsmall' style={{ height: '38px', width: '111px' }} onClick={calculateALOS}>
                                        {t('Calculate')}
                                        <ArrowRight />
                                    </button>
                                    <div className='d-flex flex-column w-100'>
                                        <label>
                                            {t('ALOS')}
                                        </label>
                                        <input
                                            readOnly
                                            value={ALOS}
                                            type='number'
                                            placeholder='0' />
                                    </div>
                                </div>
                            </div>
                            <div className='center-div'>
                                <h2>
                                    {t('What is ALOS?')}
                                </h2>
                                <p className='mt-3'>
                                    {t('ALOS, or Average Length of Stay, is a hotel performance metric that shows how many nights guests typically stay at your property. It helps hoteliers understand booking patterns, guest behavior, and the balance between short stays and longer bookings.')}
                                </p>
                                <br />
                                <p>
                                    {t('By measuring ALOS, you can gain insights into the types of guests your property attracts and determine whether your marketing, rate structure, and minimum night rules are aligned with demand.')}
                                </p>
                                <br />
                                <p>
                                    {t('Whether you’re operating a resort with extended stays or a city hotel with high turnover, ALOS gives you the data needed to optimize your business strategy.')}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='gap-3 d-flex flex-column mt-3'>
                        <h2>
                            {t('ALOS Formula')}
                        </h2>
                        <p>
                            {t('The formula for calculating ALOS is straightforward:')}
                        </p>
                        <div className='d-flex align-items-center formula gap-2'>
                            <p>
                                {t('ALOS')}  =
                            </p>
                            <div className='d-flex flex-column align-items-center'>
                                <p>
                                    {t('Total Room Nights')}
                                </p>
                                <p style={{ borderTop: '0.5px solid #1643E0' }}>
                                    {t('Total Number of Reservations')}
                                </p>
                            </div>
                        </div>
                        <p>
                            {t('To use it, divide the total number of room nights occupied by the total number of reservations over a given period. For example, if your hotel sold 750 room nights across 250 reservations, the ALOS would be 3 nights.')}
                        </p>
                        <p>
                            {t('This result means that, on average, guests stayed for 3 nights at your property. The higher the ALOS, the fewer check-ins and check-outs you need to manage, which can lead to lower operational overhead and more stable revenue per booking.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3'>
                        <h2>
                            {t('Why ALOS Matters for Hotel Performance')}
                        </h2>
                        <p>
                            {t('Knowing how long your guests typically stay can help you plan everything from staffing schedules to rate plans. A higher ALOS often reduces turnover costs and leads to more efficient operations, since longer-stay guests require fewer check-ins, check-outs, and room cleanings. It also means greater revenue per reservation and potentially more opportunities for upselling during the stay.')}
                        </p>
                        <p>
                            {t('Conversely, a lower ALOS can be beneficial during periods of high demand when short stays allow for more turnover and higher nightly rates. By tracking ALOS consistently, hotels can make informed decisions about minimum night requirements, promotional offers, and the ideal booking mix for profitability and efficiency.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('ALOS and Its Role in Hotel Strategy')}
                        </h2>
                        <p>
                            {t('ALOS is closely tied to rate strategy, revenue forecasting, and guest segmentation. If your property tends to attract short-stay guests, you may need to focus on high occupancy and dynamic pricing. If you see a rising ALOS trend, it may be time to create extended-stay packages or adjust operational resources to support longer-term guests.')}
                        </p>
                        <p>
                            {t('ALOS also helps smooth out revenue projections, as longer stays can offer more predictable occupancy and spending patterns. When combined with metrics like ADR, RevPAR, and Occupancy Rate, ALOS rounds out a complete picture of how your property is performing and who your guests really are.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('Make Length-of-Stay Work for You with Prostay')}
                        </h2>
                        <p>
                            {t('Prostay makes it easy to monitor your Average Length of Stay over time, compare guest behavior across seasons, and apply this data to your pricing strategy and marketing offers.')}
                        </p>
                        <p>
                            {t('Whether you manage a high-turnover urban hotel or a luxury beachfront resort, Prostay gives you the tools to align guest behavior with operational goals. Built-in reports, visual trends, and dynamic rule settings help ensure that you’re always optimizing for both guest value and property efficiency.')}
                        </p>
                    </div>
                </div>
                <ToolFixedMenu />
            </div>
        </div>
    )
}

export default ALOSCalculator;